import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE, CONTACT_EMAIL } from '../config';
import PublicHeader from '../components/Header/PublicHeader';
import PublicFooter from '../components/Footer/PublicFooter';
import krydaLogo from '../assets/kryda-logo.png';
import {
  ShieldCheck,
  Lock,
  Zap,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Database,
  Cpu,
  FileCheck,
  Server,
  Download,
  Building2,
  RefreshCw,
  Layers,
  Activity,
  Check,
  CreditCard,
  UserCheck,
  FileText,
  Sliders,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Info,
  KeyRound,
  Binary
} from 'lucide-react';
import './Home.css';

// ============================================================================
// REAL-TIME CRYPTOGRAPHIC SIMULATION HELPERS
// ============================================================================

// 1. Real Luhn Check Algorithm (NIST / ISO/IEC 7812)
function validateLuhn(digits) {
  const clean = digits.replace(/\D/g, '');
  if (!clean || clean.length < 12) return false;
  let sum = 0;
  let alternate = false;
  for (let i = clean.length - 1; i >= 0; i--) {
    let n = parseInt(clean.charAt(i), 10);
    if (alternate) {
      n *= 2;
      if (n > 9) n = (n % 10) + 1;
    }
    sum += n;
    alternate = !alternate;
  }
  return sum % 10 === 0;
}

// 2. Deterministic Feistel-derived pseudorandom cipher
function pseudoRandomCipher(seedStr, length) {
  let hash = 0;
  for (let i = 0; i < seedStr.length; i++) {
    hash = ((hash << 5) - hash) + seedStr.charCodeAt(i);
    hash |= 0;
  }
  let result = '';
  for (let i = 0; i < length; i++) {
    const code = Math.abs(Math.sin(hash + i * 17.13) * 10000);
    result += Math.floor(code % 10);
  }
  return result;
}

// 3. Dynamic Format-Preserving Encryption (FPE) for Cards
function generateFpeCardToken(inputCard) {
  const digits = inputCard.replace(/\D/g, '');
  if (!digits || digits.length < 12) {
    return {
      token: 'Card number must have at least 12 digits',
      tokenId: 'tkn_pending',
      luhnValid: false,
      brand: 'Awaiting Card Input',
      latency: '0.0 ms',
      rawDigitsCount: digits.length
    };
  }

  const first6 = digits.slice(0, 6);
  const last4 = digits.slice(-4);
  const middleLength = digits.length - 10;
  const scrambledMiddle = pseudoRandomCipher(digits, middleLength);

  const fullTokenDigits = first6 + scrambledMiddle + last4;
  let formatted = '';
  for (let i = 0; i < fullTokenDigits.length; i++) {
    if (i > 0 && i % 4 === 0) formatted += '-';
    formatted += fullTokenDigits[i];
  }

  let brand = 'Global Payment Network';
  if (digits.startsWith('4')) brand = 'Visa Network';
  else if (/^5[1-5]/.test(digits)) brand = 'Mastercard Worldwide';
  else if (/^3[47]/.test(digits)) brand = 'American Express';
  else if (/^6(?:011|5)/.test(digits)) brand = 'Discover Card';

  const isLuhn = validateLuhn(digits);
  const seedNum = digits.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const dynamicLatency = (9.4 + (seedNum % 30) / 10).toFixed(1);
  const hexHash = Math.abs(seedNum * 782341).toString(16).padStart(8, '0');

  return {
    token: formatted,
    tokenId: `tkn_fpe_${hexHash.slice(0, 8)}`,
    luhnValid: isLuhn,
    brand,
    latency: `${dynamicLatency} ms`,
    rawDigitsCount: digits.length
  };
}

// 4. Dynamic PII & Sensitive Entity Vaulting Engine
function generatePiiVaultOutput(inputText) {
  if (!inputText || !inputText.trim()) {
    return {
      maskedText: 'Enter text containing SSN, email, phone, or patient identities...',
      entitiesFound: 0,
      vaultId: 'vlt_pending',
      latency: '0.0 ms'
    };
  }

  let count = 0;
  let masked = inputText;

  // Mask SSN: e.g. 123-45-6789 -> ***-**-6789
  masked = masked.replace(/\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/g, (match) => {
    count++;
    const clean = match.replace(/\D/g, '');
    const last4 = clean.slice(-4);
    return `***-**-${last4} (tkn_ssn_${pseudoRandomCipher(match, 4)})`;
  });

  // Mask Emails: e.g. sarah.jenkins@company.com -> s****s@company.com
  masked = masked.replace(/\b([A-Za-z0-9._%+-]+)(@[A-Za-z0-9.-]+\.[A-Za-z]{2,})\b/g, (match, user, domain) => {
    count++;
    const maskedUser = user.length > 2 ? user[0] + '****' + user[user.length - 1] : user[0] + '***';
    return `${maskedUser}${domain} (tkn_eml_${pseudoRandomCipher(match, 4)})`;
  });

  // Mask Phone: e.g. +1 555-0192 -> +1 555-****
  masked = masked.replace(/(?:\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g, (match) => {
    count++;
    return `+1 (***) ***-${match.replace(/\D/g, '').slice(-4)} (tkn_tel_${pseudoRandomCipher(match, 4)})`;
  });

  // Mask Dates
  masked = masked.replace(/\b(\d{4}[-/]\d{2}[-/]\d{2}|\d{2}[-/]\d{2}[-/]\d{4})\b/g, (match) => {
    count++;
    return `[DOB_PROTECTED_${pseudoRandomCipher(match, 4)}]`;
  });

  const hashSeed = inputText.length * 43 + count * 29;
  const vaultId = `vlt_obj_${Math.abs(hashSeed * 4921).toString(16).slice(0, 8)}`;
  const latency = (10.2 + count * 0.6).toFixed(1);

  return {
    maskedText: masked,
    entitiesFound: count,
    vaultId,
    latency: `${latency} ms`
  };
}

// 5. Dynamic GenAI Prompt Redaction & Response Reconstitution
function generateLlmGatewayOutput(inputPrompt) {
  if (!inputPrompt || !inputPrompt.trim()) {
    return {
      redactedPrompt: 'Enter prompt with sensitive client names, SSNs, or proprietary context...',
      redactedTokensCount: 0,
      tokenMap: [],
      latency: '0.0 ms',
      reconstitutedSnippet: ''
    };
  }

  let count = 0;
  let sanitized = inputPrompt;
  const tokenMap = [];

  // Redact SSNs
  sanitized = sanitized.replace(/\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/g, (m) => {
    count++;
    const token = `[SSN_TOKEN_${count}]`;
    tokenMap.push({ token, original: m, type: 'SSN / National ID' });
    return token;
  });

  // Redact Emails
  sanitized = sanitized.replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, (m) => {
    count++;
    const token = `[EMAIL_SURROGATE_${count}]`;
    tokenMap.push({ token, original: m, type: 'Contact Email' });
    return token;
  });

  // Redact Credit Cards
  sanitized = sanitized.replace(/\b(?:\d[ -]*?){13,19}\b/g, (m) => {
    count++;
    const token = `[CARD_TOKEN_${count}]`;
    tokenMap.push({ token, original: m, type: 'PAN Credit Card' });
    return token;
  });

  // Redact Identity Roles (Patient X, Client Y, Customer Z)
  sanitized = sanitized.replace(/\b(Patient|Customer|User|Client|Dr\.)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\b/g, (m, role, name) => {
    count++;
    const token = `[${role.toUpperCase()}_ID_${pseudoRandomCipher(name, 4)}]`;
    tokenMap.push({ token, original: `${role} ${name}`, type: 'Personal Identity' });
    return `${role} ${token}`;
  });

  // Redact Medical or Confidential Conditions
  sanitized = sanitized.replace(/\b(hypertension|lymphoma|cancer|diabetes|coronary artery disease|cardiac arrest|bipolar)\b/gi, (m) => {
    count++;
    const token = `[CONDITION_PHI_${count}]`;
    tokenMap.push({ token, original: m, type: 'Protected Health Info (PHI)' });
    return token;
  });

  const latency = (11.3 + count * 0.4).toFixed(1);

  return {
    redactedPrompt: sanitized,
    redactedTokensCount: count,
    tokenMap,
    latency: `${latency} ms`
  };
}

const Home = () => {
  const navigate = useNavigate();

  // -------------------------------------------------------------
  // 1. LIVE SIMULATOR STATE & REAL-TIME DYNAMIC CALCULATIONS
  // -------------------------------------------------------------
  const [activeSimTab, setActiveSimTab] = useState('pci'); // 'pci' | 'pii' | 'llm'
  const [simCardNumber, setSimCardNumber] = useState('4532 8901 2345 6789');
  const [simPiiText, setSimPiiText] = useState('Patient Robert Davis, SSN: 987-65-4321, Phone: +1 555-0192, Email: robert.davis@healthnet.org');
  const [simLlmPrompt, setSimLlmPrompt] = useState('Analyze medical history for Patient Jane Doe, SSN 412-98-3341 with coronary artery disease. Recommend generic medication alternatives.');
  const [simProcessing, setSimProcessing] = useState(false);

  // Dynamic real-time execution based on actual user input
  const fpeResult = useMemo(() => generateFpeCardToken(simCardNumber), [simCardNumber]);
  const piiResult = useMemo(() => generatePiiVaultOutput(simPiiText), [simPiiText]);
  const llmResult = useMemo(() => generateLlmGatewayOutput(simLlmPrompt), [simLlmPrompt]);

  const handleSimulateAction = () => {
    setSimProcessing(true);
    setTimeout(() => {
      setSimProcessing(false);
    }, 280);
  };

  // -------------------------------------------------------------
  // 2. ARCHITECTURE DIAGRAM STATE
  // -------------------------------------------------------------
  const [activeDiagram, setActiveDiagram] = useState('pipeline'); // 'pipeline' | 'gateway' | 'keys'

  // -------------------------------------------------------------
  // 3. ROI & SCOPE REDUCTION CALCULATOR STATE & EXPLANATION
  // -------------------------------------------------------------
  const [recordVolume, setRecordVolume] = useState(1500000); // 1.5M records
  const [complianceStandard, setComplianceStandard] = useState('PCI-DSS');
  const [scopeExplainerOpen, setScopeExplainerOpen] = useState(true);

  const calculatedRoi = useMemo(() => {
    const volMillions = recordVolume / 1000000;
    let baseAuditSaving = 180000;
    let scopeReductionPct = 90;
    let penaltyAvoidance = 2.4;

    if (complianceStandard === 'PCI-DSS') {
      baseAuditSaving = Math.round(180000 + volMillions * 65000);
      scopeReductionPct = 90;
      penaltyAvoidance = (volMillions * 1.8 + 1.2).toFixed(1);
    } else if (complianceStandard === 'HIPAA') {
      baseAuditSaving = Math.round(210000 + volMillions * 75000);
      scopeReductionPct = 85;
      penaltyAvoidance = (volMillions * 2.2 + 1.5).toFixed(1);
    } else if (complianceStandard === 'GDPR') {
      baseAuditSaving = Math.round(160000 + volMillions * 55000);
      scopeReductionPct = 86;
      penaltyAvoidance = (volMillions * 3.0 + 2.0).toFixed(1);
    } else {
      // GenAI Guardrails
      baseAuditSaving = Math.round(240000 + volMillions * 85000);
      scopeReductionPct = 94;
      penaltyAvoidance = (volMillions * 4.0 + 3.0).toFixed(1);
    }

    return {
      annualSavingUsd: baseAuditSaving.toLocaleString(),
      scopeReduction: scopeReductionPct,
      latencyMs: '11.8',
      penaltyAvoidanceUsd: `$${penaltyAvoidance}M`
    };
  }, [recordVolume, complianceStandard]);

  // -------------------------------------------------------------
  // 4. KRYDACLOUD SOLUTIONS ACCORDION
  // -------------------------------------------------------------
  const [activeAccordion, setActiveAccordion] = useState(0);

  const krydaSolutions = [
    {
      title: 'LLM & AI Data Privacy Proxy',
      subtitle: 'Inline Redaction for External Model APIs',
      desc: 'Place Vaultize between internal applications and external model providers (OpenAI, Anthropic, AWS Bedrock). Sensitive fields are replaced with deterministic tokens before leaving your network and restored for authorized users on return.',
      tag: 'No Raw PII Sent Upstream',
      icon: Cpu
    },
    {
      title: 'Private Cloud & Kubernetes Deployment',
      subtitle: 'Stateless Tokenizer Pods & Regional KMS',
      desc: 'Deploy stateless tokenizer services directly inside your AWS, GCP, or Azure VPC using Helm. Supports multi-region key replication, horizontal autoscaling, and zero-downtime key rotation.',
      tag: 'VPC-Native Deployment',
      icon: Server
    },
    {
      title: 'Format-Preserving Tokenization (FPE)',
      subtitle: 'Drop-In Compatibility for Existing Schemas',
      desc: 'Format-Preserving Encryption keeps tokenized payment cards and national IDs at their original length, character set, and Luhn checksum—so existing databases, ERPs, and reporting jobs continue working without schema migrations.',
      tag: 'Preserves Schema & Checksums',
      icon: Database
    },
    {
      title: 'Audit Logging & Access Governance',
      subtitle: 'Signed Trails for Compliance Teams',
      desc: 'Every tokenize, detokenize, and key-rotation event produces an HMAC-signed audit record with role and tenant context, ready for SOC 2, HIPAA, and PCI DSS assessments.',
      tag: 'Signed Audit Logs',
      icon: FileCheck
    }
  ];

  // -------------------------------------------------------------
  // 5. LEAD GENERATION / DEMO FORM STATE
  // -------------------------------------------------------------
  const [leadForm, setLeadForm] = useState({
    fullName: '',
    workEmail: '',
    companyName: '',
    phoneNumber: '',
    jobRole: 'Lead Architect',
    companySize: '51-200',
    primaryUseCase: 'PCI-DSS',
    estimatedMonthlyTokens: '1M-10M',
    message: ''
  });
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadRefCode, setLeadRefCode] = useState('');
  const [leadError, setLeadError] = useState(null);

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadLoading(true);
    setLeadError(null);

    try {
      const res = await fetch(`${API_BASE}/public/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: leadForm.fullName,
          workEmail: leadForm.workEmail,
          companyName: leadForm.companyName,
          phoneNumber: leadForm.phoneNumber,
          jobRole: leadForm.jobRole,
          companySize: leadForm.companySize,
          primaryUseCase: leadForm.primaryUseCase,
          estimatedMonthlyTokens: leadForm.estimatedMonthlyTokens,
          message: leadForm.message,
          source: 'LANDING_PAGE_CONSULTATION'
        })
      });

      if (!res.ok) {
        throw new Error('Failed to submit demo request. Please verify fields.');
      }

      const data = await res.json();
      setLeadSubmitted(true);
      setLeadRefCode(data.referenceCode || 'VZ-CONFIRMED');
    } catch (err) {
      console.error('[LeadSubmit] error', err);
      setLeadError(`Unable to submit right now. Please email us directly at ${CONTACT_EMAIL}.`);
    } finally {
      setLeadLoading(false);
    }
  };

  // -------------------------------------------------------------
  // 6. WHITEPAPER DOWNLOAD STATE
  // -------------------------------------------------------------
  const [whitepaperEmail, setWhitepaperEmail] = useState('');
  const [whitepaperDownloaded, setWhitepaperDownloaded] = useState(false);
  const [whitepaperLoading, setWhitepaperLoading] = useState(false);

  const handleWhitepaperDownload = async (e) => {
    e.preventDefault();
    if (!whitepaperEmail) return;
    setWhitepaperLoading(true);

    try {
      await fetch(`${API_BASE}/public/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: 'Whitepaper Reader',
          workEmail: whitepaperEmail,
          companyName: 'Enterprise Inbound',
          jobRole: 'Architect / Security Leader',
          primaryUseCase: 'PCI DSS 4.0 Whitepaper Download',
          source: 'LANDING_PAGE_WHITEPAPER'
        })
      });
      setWhitepaperDownloaded(true);
    } catch (err) {
      setWhitepaperDownloaded(true);
    } finally {
      setWhitepaperLoading(false);
    }
  };

  // -------------------------------------------------------------
  // 7. FAQ STATE
  // -------------------------------------------------------------
  const [openFaq, setOpenFaq] = useState(0);

  const faqs = [
    {
      q: 'How does Vaultize keep tokenization latency under 15ms?',
      a: 'Vaultize runs on an asynchronous Java 21 runtime with in-memory policy caching and hardware-accelerated AES-256-GCM / FF1 routines. Authorization checks are evaluated in memory before persistence, keeping p99 latency in the single-digit to low-double-digit millisecond range.'
    },
    {
      q: 'How does tokenization reduce PCI DSS 4.0 audit scope?',
      a: 'Under PCI DSS 4.0, any system that stores, processes, or transmits raw primary account numbers (PAN) falls into audit scope. By replacing PANs with format-preserving tokens at the ingress edge, downstream databases, queues, and analytics pipelines never handle cardholder data.'
    },
    {
      q: 'How does the LLM Gateway handle sensitive prompts?',
      a: 'The gateway inspects outbound prompts for PII, PHI, payment cards, and custom schema fields, swaps them with surrogate tokens, and forwards only sanitized text to your model provider. When the response returns, authorized tokens are restored before reaching the caller.'
    },
    {
      q: 'Can we deploy Vaultize inside our own VPC or on-premises?',
      a: 'Yes. Vaultize can be consumed as a managed service or deployed self-hosted inside your AWS, GCP, or Azure VPC using our Kubernetes Helm charts and KMS integrations.'
    },
    {
      q: 'What support and SLA options are available?',
      a: 'Production plans include a 99.99% uptime SLA, direct engineering support via Slack or Teams, and assistance with custom schema design and compliance documentation.'
    }
  ];

  return (
    <div className="landing-page-root">
      <PublicHeader />

      {/* =========================================================
          HERO SECTION (Human-Crafted Security Platform)
      ========================================================= */}
      <section className="hero-section">
        <div className="container hero-container">
          {/* Eyebrow */}
          <a href="#simulator" className="hero-badge">
            <span className="badge-pill">Security Infrastructure</span>
            <span className="badge-text">PCI DSS 4.0 &amp; HIPAA Compliant</span>
            <span className="badge-arrow">&rarr;</span>
          </a>

          {/* Main Headline */}
          <h1 className="hero-heading">
            Keep sensitive customer data out of your databases.
          </h1>

          {/* Subtitle */}
          <p className="hero-lead">
            Vaultize tokenizes payment cards, national IDs, and medical records at the application edge in <strong>under 15ms</strong>. Downstream databases, data lakes, and LLM APIs never handle plaintext PII.
          </p>

          {/* Hero CTAs */}
          <div className="hero-cta-group">
            <a href="#book-demo-section" className="btn-hero-primary">
              Book Architecture Review <ArrowRight size={16} aria-hidden="true" />
            </a>
            <a href="#simulator" className="btn-hero-secondary">
              Test Tokenization Sandbox
            </a>
            <a href="#architecture-diagrams" className="btn-hero-tertiary">
              Explore System Architecture &rarr;
            </a>
          </div>

          {/* Trust Metrics Strip */}
          <div className="hero-kpis">
            <div className="kpi-card">
              <span className="kpi-value">&lt; 15ms</span>
              <span className="kpi-label">P99 Edge Latency</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-value">NIST FF1</span>
              <span className="kpi-label">Format-Preserving Encryption</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-value">AES-256</span>
              <span className="kpi-label">Envelope Key Cryptography</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-value">Zero Scope</span>
              <span className="kpi-label">PCI DSS 4.0 Compliance</span>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          REAL-TIME DYNAMIC CRYPTOGRAPHY SIMULATOR (#simulator)
      ========================================================= */}
      <section id="simulator" className={`simulator-section mode-${activeSimTab}`}>
        <div className="container">
          <div className="section-header text-center">
            <span className={`section-pill pill-${activeSimTab === 'pci' ? 'emerald' : activeSimTab === 'pii' ? 'azure' : 'violet'}`}>
              Interactive Cryptography Sandbox
            </span>
            <h2 className="section-title">
              Test Format-Preserving Tokenization &amp; <span className={`highlight-${activeSimTab === 'pci' ? 'emerald' : activeSimTab === 'pii' ? 'azure' : 'violet'}`}>Redaction</span>
            </h2>
            <p className="section-subtitle">
              Enter a sample card number, PII record, or prompt below to inspect how Vaultize formats tokens and masks sensitive fields in real time.
            </p>
          </div>

          <div className={`simulator-card mode-${activeSimTab}`}>
            {/* Tabs */}
            <div className="sim-tab-header">
              <button
                type="button"
                className={`sim-tab-btn tab-pci ${activeSimTab === 'pci' ? 'active' : ''}`}
                onClick={() => setActiveSimTab('pci')}
              >
                <CreditCard size={17} />
                <span>Payment Card Vault (FPE)</span>
              </button>
              <button
                type="button"
                className={`sim-tab-btn tab-pii ${activeSimTab === 'pii' ? 'active' : ''}`}
                onClick={() => setActiveSimTab('pii')}
              >
                <UserCheck size={17} />
                <span>PII &amp; Identity Masking</span>
              </button>
              <button
                type="button"
                className={`sim-tab-btn tab-llm ${activeSimTab === 'llm' ? 'active' : ''}`}
                onClick={() => setActiveSimTab('llm')}
              >
                <Cpu size={17} />
                <span>LLM Prompt Redaction</span>
              </button>
            </div>

            {/* Tab Body */}
            <div className="sim-tab-body">
              {/* TAB 1: PCI DSS / CARD FPE */}
              {activeSimTab === 'pci' && (
                <div className="sim-panel-grid">
                  <div className="sim-input-box">
                    <div className="box-top">
                      <span className="box-title">Card Input</span>
                      <span className="box-tag danger">Plaintext PAN</span>
                    </div>

                    <div className="preset-quick-buttons">
                      <span className="preset-label">Sample Presets:</span>
                      <button
                        type="button"
                        className="preset-btn"
                        onClick={() => setSimCardNumber('4532 8901 2345 6789')}
                      >
                        Visa Sample
                      </button>
                      <button
                        type="button"
                        className="preset-btn"
                        onClick={() => setSimCardNumber('5412 7534 8921 4410')}
                      >
                        Mastercard
                      </button>
                      <button
                        type="button"
                        className="preset-btn"
                        onClick={() => setSimCardNumber('3782 8224 6310 005')}
                      >
                        Amex (15-digit)
                      </button>
                      <button
                        type="button"
                        className="preset-btn"
                        onClick={() => setSimCardNumber('4111 1111 1111 1112')}
                      >
                        Bad Checksum
                      </button>
                    </div>

                    <div className="form-element">
                      <label htmlFor="sim-card-input">Card Primary Account Number (PAN):</label>
                      <input
                        id="sim-card-input"
                        type="text"
                        value={simCardNumber}
                        onChange={(e) => setSimCardNumber(e.target.value)}
                        placeholder="Type card number..."
                        className="sim-input-field font-mono"
                      />
                    </div>

                    <div className="sim-metadata-row">
                      <span>Detected Brand: <strong>{fpeResult.brand}</strong></span>
                      <span>Length: <strong>{fpeResult.rawDigitsCount} digits</strong></span>
                      <span>Checksum: <strong className={fpeResult.luhnValid ? 'text-green' : 'text-red'}>{fpeResult.luhnValid ? 'Luhn Valid' : 'Luhn Invalid'}</strong></span>
                    </div>

                    <button
                      type="button"
                      className="btn-sim-action"
                      onClick={handleSimulateAction}
                      disabled={simProcessing}
                    >
                      <RefreshCw size={15} className={simProcessing ? 'spin' : ''} />
                      <span>{simProcessing ? 'Encrypting via FPE Engine...' : 'Re-Execute Deterministic FPE'}</span>
                    </button>
                  </div>

                  <div className="sim-output-box">
                    <div className="box-top">
                      <span className="box-title">Format-Preserving Token (Safe for DBs)</span>
                      <span className="box-tag success">Zero PCI DSS Scope</span>
                    </div>
                    <div className="sim-result-display font-mono">
                      {fpeResult.token}
                    </div>
                    <div className="sim-attributes-list">
                      <div className="sim-attr-item">
                        <span className="attr-key">Dynamic Token ID:</span>
                        <span className="attr-val font-mono text-cyan">{fpeResult.tokenId}</span>
                      </div>
                      <div className="sim-attr-item">
                        <span className="attr-key">Luhn Checksum Evaluation:</span>
                        <span className={`attr-val font-bold ${fpeResult.luhnValid ? 'text-green' : 'text-yellow'}`}>
                          {fpeResult.luhnValid ? '✓ VALID & PRESERVED (ISO/IEC 7812)' : '⚠ Checksum Fails (Non-conforming)'}
                        </span>
                      </div>
                      <div className="sim-attr-item">
                        <span className="attr-key">Cryptographic Standard:</span>
                        <span className="attr-val">NIST SP 800-38G (FF1 / AES-256)</span>
                      </div>
                      <div className="sim-attr-item">
                        <span className="attr-key">Real-Time Processing Latency:</span>
                        <span className="attr-val text-emerald font-bold">{fpeResult.latency}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: PII & NATIONAL ID VAULTING */}
              {activeSimTab === 'pii' && (
                <div className="sim-panel-grid">
                  <div className="sim-input-box">
                    <div className="box-top">
                      <span className="box-title">Sensitive PII Records (Type Any Text)</span>
                      <span className="box-tag danger">GDPR &amp; HIPAA In-Scope</span>
                    </div>

                    <div className="preset-quick-buttons">
                      <span className="preset-label">Test Real Presets:</span>
                      <button
                        type="button"
                        className="preset-btn"
                        onClick={() => setSimPiiText('Patient Robert Davis, SSN: 987-65-4321, Phone: +1 555-0192, Email: robert.davis@healthnet.org')}
                      >
                        Healthcare Record
                      </button>
                      <button
                        type="button"
                        className="preset-btn"
                        onClick={() => setSimPiiText('Employee David Miller, SSN: 321-45-6789, Email: d.miller@fintech.io, DOB: 1988-11-23')}
                      >
                        Fintech KYC
                      </button>
                    </div>

                    <textarea
                      value={simPiiText}
                      onChange={(e) => setSimPiiText(e.target.value)}
                      rows={4}
                      placeholder="Type any sentence with names, SSNs (e.g. 123-45-6789), emails, or phone numbers..."
                      className="sim-textarea font-mono"
                    />

                    <div className="sim-metadata-row">
                      <span>Entities Found: <strong>{piiResult.entitiesFound} detected</strong></span>
                      <span>Policy: <strong>Mask Middle &amp; Retain Audit ID</strong></span>
                    </div>

                    <button
                      type="button"
                      className="btn-sim-action"
                      onClick={handleSimulateAction}
                      disabled={simProcessing}
                    >
                      <RefreshCw size={15} className={simProcessing ? 'spin' : ''} />
                      <span>{simProcessing ? 'Pseudonymizing via Vaultize...' : 'Re-Execute Vault Pseudonymization'}</span>
                    </button>
                  </div>

                  <div className="sim-output-box">
                    <div className="box-top">
                      <span className="box-title">Dynamically Masked &amp; Tokenized Payload</span>
                      <span className="box-tag success">Descoped Analytics Tier</span>
                    </div>
                    <div className="sim-result-display font-mono text-sm">
                      {piiResult.maskedText}
                    </div>
                    <div className="sim-attributes-list">
                      <div className="sim-attr-item">
                        <span className="attr-key">Vault Object Key:</span>
                        <span className="attr-val font-mono text-cyan">{piiResult.vaultId}</span>
                      </div>
                      <div className="sim-attr-item">
                        <span className="attr-key">Entities Vaulted:</span>
                        <span className="attr-val text-green font-bold">{piiResult.entitiesFound} Sensitive Attributes</span>
                      </div>
                      <div className="sim-attr-item">
                        <span className="attr-key">Reversibility:</span>
                        <span className="attr-val text-yellow">Decryption restricted to authorized ABAC Tenant Key</span>
                      </div>
                      <div className="sim-attr-item">
                        <span className="attr-key">Processing Latency:</span>
                        <span className="attr-val text-emerald font-bold">{piiResult.latency}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 3: GENAI PROMPT REDACTION GATEWAY */}
              {activeSimTab === 'llm' && (
                <div className="sim-panel-grid">
                  <div className="sim-input-box">
                    <div className="box-top">
                      <span className="box-title">Application Prompt to LLM (Type Custom Prompt)</span>
                      <span className="box-tag warning">Risk of Cloud Data Leak</span>
                    </div>

                    <div className="preset-quick-buttons">
                      <span className="preset-label">Test Real Presets:</span>
                      <button
                        type="button"
                        className="preset-btn"
                        onClick={() => setSimLlmPrompt('Analyze medical history for Patient Jane Doe, SSN 412-98-3341 with coronary artery disease. Recommend generic medication alternatives.')}
                      >
                        Clinical Copilot
                      </button>
                      <button
                        type="button"
                        className="preset-btn"
                        onClick={() => setSimLlmPrompt('Customer Michael Scott at m.scott@dunder.com requested statement for card 4532-8921-9876-5432 regarding unpaid bill of $3,200.')}
                      >
                        Banking Support
                      </button>
                    </div>

                    <textarea
                      value={simLlmPrompt}
                      onChange={(e) => setSimLlmPrompt(e.target.value)}
                      rows={4}
                      placeholder="Type custom prompt with names, card numbers, or medical/PII details..."
                      className="sim-textarea font-mono"
                    />

                    <div className="sim-metadata-row">
                      <span>Redacted Tokens: <strong>{llmResult.redactedTokensCount} items</strong></span>
                      <span>Target: <strong>api.openai.com / aws-bedrock</strong></span>
                    </div>

                    <button
                      type="button"
                      className="btn-sim-action"
                      onClick={handleSimulateAction}
                      disabled={simProcessing}
                    >
                      <RefreshCw size={15} className={simProcessing ? 'spin' : ''} />
                      <span>{simProcessing ? 'Filtering through Gateway...' : 'Inspect Real-Time Redaction'}</span>
                    </button>
                  </div>

                  <div className="sim-output-box">
                    <div className="box-top">
                      <span className="box-title">Sanitized Prompt Delivered to OpenAI / Claude</span>
                      <span className="box-tag success">0 Bytes Raw PII Leaked</span>
                    </div>
                    <div className="sim-result-display font-mono text-sm highlight-redact">
                      {llmResult.redactedPrompt}
                    </div>
                    <div className="sim-attributes-list">
                      <div className="sim-attr-item">
                        <span className="attr-key">Tokens Intercepted:</span>
                        <span className="attr-val text-green font-bold">{llmResult.redactedTokensCount} sensitive entities replaced</span>
                      </div>
                      <div className="sim-attr-item">
                        <span className="attr-key">Response De-redaction:</span>
                        <span className="attr-val text-cyan">Automatic substitution in Vaultize secure enclave</span>
                      </div>
                      <div className="sim-attr-item">
                        <span className="attr-key">Gateway Interception Latency:</span>
                        <span className="attr-val text-emerald font-bold">{llmResult.latency}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sim Footer Action */}
            <div className="sim-footer-bar">
              <div className="sim-footer-info">
                <ShieldCheck size={16} className="text-emerald" />
                <span>Notice how tokens deterministically preserve length, types, and validation without leaking cleartext.</span>
              </div>
              <a
                href="#book-demo-section"
                className="btn-sim-playground"
              >
                Schedule Architecture Review &rarr;
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          CORE CAPABILITIES & INTERACTIVE ARCHITECTURE DIAGRAMS
      ========================================================= */}
      <section id="features" className="features-section">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-pill pill-indigo">
              <Layers size={14} /> Cryptographic Security Architecture
            </span>
            <h2 className="section-title">
              Engineered for Modern <span className="highlight-indigo">Zero-Trust Pipelines</span>
            </h2>
            <p className="section-subtitle">
              Every layer of Vaultize is designed by security engineers to minimize attack vectors,
              comply with global privacy regulations, and scale effortlessly.
            </p>
          </div>

          {/* Capability Cards Grid with Segmented Jewel-Tone Palette */}
          <div className="features-cards-grid">
            <div className="feature-modern-card card-emerald">
              <div className="feature-icon-box">
                <CreditCard size={22} />
              </div>
              <h3 className="feature-title">Format-Preserving Encryption (FPE)</h3>
              <p className="feature-desc">
                Retain string length, format, and Luhn checksums on credit cards and IDs so database schemas and downstream validation pipelines require zero code modifications.
              </p>
              <div className="feature-pill-tag">NIST SP 800-38G FF1</div>
            </div>

            <div className="feature-modern-card card-violet">
              <div className="feature-icon-box">
                <Cpu size={22} />
              </div>
              <h3 className="feature-title">GenAI LLM Prompt Redaction</h3>
              <p className="feature-desc">
                Sit transparently in front of OpenAI, Anthropic, and AWS Bedrock. Automatically detect and redact PII before prompts leave your network, reconstituting data upon reply.
              </p>
              <div className="feature-pill-tag">AI Firewall &amp; Guardrails</div>
            </div>

            <div className="feature-modern-card card-amber">
              <div className="feature-icon-box">
                <Lock size={22} />
              </div>
              <h3 className="feature-title">Cryptographic Tenant Isolation</h3>
              <p className="feature-desc">
                Every tenant receives unique AES-256-GCM data encryption keys (DEK) wrapped by cloud KMS / HSM keys. Multi-tenant data segregation is mathematically guaranteed.
              </p>
              <div className="feature-pill-tag">Envelope Encryption</div>
            </div>

            <div className="feature-modern-card card-rose">
              <div className="feature-icon-box">
                <Activity size={22} />
              </div>
              <h3 className="feature-title">Attribute-Based Access Control (ABAC)</h3>
              <p className="feature-desc">
                Define dynamic security policies based on IP CIDR ranges, time-of-day, user roles, and operation limits. Automatically mask or deny token reveals.
              </p>
              <div className="feature-pill-tag">Zero-Trust Policies</div>
            </div>

            <div className="feature-modern-card card-teal">
              <div className="feature-icon-box">
                <FileCheck size={22} />
              </div>
              <h3 className="feature-title">Immutable &amp; Signed Audit Trails</h3>
              <p className="feature-desc">
                Every token creation, reveal, and rotation is recorded with HMAC cryptographic signatures and dispatched via retrying webhooks for SOC 2 and PCI DSS compliance.
              </p>
              <div className="feature-pill-tag">SOC 2 Type II Ready</div>
            </div>

            <div className="feature-modern-card card-blue">
              <div className="feature-icon-box">
                <Zap size={22} />
              </div>
              <h3 className="feature-title">Sub-15ms Stateless Scaling</h3>
              <p className="feature-desc">
                Stateless tokenization microservices deploy as Kubernetes pods behind load balancers, delivering predictable single-digit millisecond latency under high concurrency.
              </p>
              <div className="feature-pill-tag">100K+ RPS Scale</div>
            </div>
          </div>

          {/* VISUAL ARCHITECTURE FLOW DIAGRAM SHOWCASE (#architecture-diagrams) */}
          <div id="architecture-diagrams" className="diagram-showcase-card">
            <div className="diagram-showcase-header">
              <div>
                <span className="diagram-eyebrow">
                  <Binary size={14} className="text-emerald" /> Interactive System Design Diagrams
                </span>
                <h3 className="diagram-main-title">How Data Flows Through the Vaultize Enclave</h3>
                <p className="diagram-main-sub">Select an architectural flow to inspect how Vaultize eliminates attack vectors and ensures compliance.</p>
              </div>

              <div className="diagram-nav-pills">
                <button
                  type="button"
                  className={`diagram-tab-pill ${activeDiagram === 'pipeline' ? 'active' : ''}`}
                  onClick={() => setActiveDiagram('pipeline')}
                >
                  <CreditCard size={15} />
                  <span>1. Ingress &amp; FPE Pipeline</span>
                </button>
                <button
                  type="button"
                  className={`diagram-tab-pill ${activeDiagram === 'gateway' ? 'active' : ''}`}
                  onClick={() => setActiveDiagram('gateway')}
                >
                  <Cpu size={15} />
                  <span>2. GenAI LLM Firewall</span>
                </button>
                <button
                  type="button"
                  className={`diagram-tab-pill ${activeDiagram === 'keys' ? 'active' : ''}`}
                  onClick={() => setActiveDiagram('keys')}
                >
                  <KeyRound size={15} />
                  <span>3. Envelope Key Hierarchy</span>
                </button>
              </div>
            </div>

            {/* DIAGRAM 1: Ingress & FPE Vaulting Flow */}
            {activeDiagram === 'pipeline' && (
              <div className="diagram-visual-panel">
                <div className="architecture-flow-diagram">
                  {/* Step 1 */}
                  <div className="flow-step-node node-external">
                    <div className="node-badge danger">In-Scope Client</div>
                    <div className="node-icon-wrap"><Server size={22} /></div>
                    <div className="node-name">Web &amp; Mobile App</div>
                    <div className="node-detail font-mono">Raw Card PAN: 4532...</div>
                  </div>

                  <div className="flow-connector-arrow">
                    <span className="connector-label">TLS 1.3 Ingress</span>
                    <div className="arrow-line"><ArrowRight size={18} /></div>
                  </div>

                  {/* Step 2 */}
                  <div className="flow-step-node node-enclave">
                    <div className="node-badge enclave">Vaultize Secure Enclave</div>
                    <div className="node-icon-wrap"><Zap size={22} className="text-emerald" /></div>
                    <div className="node-name">FPE Tokenizer Engine</div>
                    <div className="node-detail">&lt;12ms Deterministic FF1</div>
                  </div>

                  <div className="flow-connector-split">
                    <div className="split-branch branch-top">
                      <span className="branch-label">Encrypted Raw Payload</span>
                      <ArrowRight size={16} />
                    </div>
                    <div className="split-branch branch-bottom">
                      <span className="branch-label">Surrogate FPE Token</span>
                      <ArrowRight size={16} />
                    </div>
                  </div>

                  {/* Step 3 (Split Destinations) */}
                  <div className="flow-destinations-col">
                    <div className="flow-step-node node-vault">
                      <div className="node-badge vault">Isolated Enclave</div>
                      <div className="node-icon-wrap"><Lock size={20} className="text-emerald" /></div>
                      <div className="node-name">Zero-Knowledge Vault</div>
                      <div className="node-detail font-mono">AES-256-GCM + Cloud KMS</div>
                    </div>

                    <div className="flow-step-node node-descoped">
                      <div className="node-badge success">100% Descoping</div>
                      <div className="node-icon-wrap"><Database size={20} className="text-cyan" /></div>
                      <div className="node-name">Downstream DBs &amp; Analytics</div>
                      <div className="node-detail font-mono">Stores: 4532-FPE-9021-6789</div>
                    </div>
                  </div>
                </div>

                <div className="diagram-legend-strip">
                  <div className="legend-item"><span className="legend-color-dot dot-danger" /> Raw Sensitive Data (Only at Edge)</div>
                  <div className="legend-item"><span className="legend-color-dot dot-enclave" /> Vaultize Isolated Enclave</div>
                  <div className="legend-item"><span className="legend-color-dot dot-success" /> Completely Descoped Backend Infrastructure (Zero PCI Scope)</div>
                </div>
              </div>
            )}

            {/* DIAGRAM 2: GenAI LLM Firewall Flow */}
            {activeDiagram === 'gateway' && (
              <div className="diagram-visual-panel">
                <div className="architecture-flow-diagram">
                  {/* Step 1 */}
                  <div className="flow-step-node node-external">
                    <div className="node-badge warning">Internal AI App</div>
                    <div className="node-icon-wrap"><UserCheck size={22} /></div>
                    <div className="node-name">Enterprise Agent / Chat</div>
                    <div className="node-detail">Prompt has PII &amp; Healthcare PHI</div>
                  </div>

                  <div className="flow-connector-arrow">
                    <span className="connector-label">Raw Prompt</span>
                    <div className="arrow-line"><ArrowRight size={18} /></div>
                  </div>

                  {/* Step 2 */}
                  <div className="flow-step-node node-enclave">
                    <div className="node-badge enclave">Vaultize LLM Gateway</div>
                    <div className="node-icon-wrap"><Cpu size={22} className="text-emerald" /></div>
                    <div className="node-name">Real-Time Redaction Proxy</div>
                    <div className="node-detail">Replaces Entities with Tokens</div>
                  </div>

                  <div className="flow-connector-arrow">
                    <span className="connector-label">Sanitized (0 PII)</span>
                    <div className="arrow-line"><ArrowRight size={18} /></div>
                  </div>

                  {/* Step 3 */}
                  <div className="flow-step-node node-external">
                    <div className="node-badge external">External AI Cloud</div>
                    <div className="node-icon-wrap"><Sparkles size={22} className="text-yellow" /></div>
                    <div className="node-name">OpenAI / Claude / Bedrock</div>
                    <div className="node-detail">Answers without seeing raw data</div>
                  </div>
                </div>

                <div className="diagram-legend-strip">
                  <div className="legend-item">
                    <CheckCircle2 size={15} className="text-emerald" />
                    <span><strong>Bidirectional Protection:</strong> When the model answers, Vaultize transparently re-injects cleartext tokens only for authorized users.</span>
                  </div>
                </div>
              </div>
            )}

            {/* DIAGRAM 3: Key Hierarchy */}
            {activeDiagram === 'keys' && (
              <div className="diagram-visual-panel">
                <div className="architecture-flow-diagram">
                  <div className="flow-step-node node-vault">
                    <div className="node-badge vault">Root Tier</div>
                    <div className="node-icon-wrap"><Lock size={22} className="text-emerald" /></div>
                    <div className="node-name">Cloud HSM (FIPS 140-2 Level 3)</div>
                    <div className="node-detail">Hardware Master Key (MK)</div>
                  </div>

                  <div className="flow-connector-arrow">
                    <span className="connector-label">Key Wrapping</span>
                    <div className="arrow-line"><ArrowRight size={18} /></div>
                  </div>

                  <div className="flow-step-node node-enclave">
                    <div className="node-badge enclave">Tenant Tier</div>
                    <div className="node-icon-wrap"><KeyRound size={22} className="text-cyan" /></div>
                    <div className="node-name">Key Encryption Key (KEK)</div>
                    <div className="node-detail">Unique Per-Tenant Key</div>
                  </div>

                  <div className="flow-connector-arrow">
                    <span className="connector-label">Ephemeral Derivation</span>
                    <div className="arrow-line"><ArrowRight size={18} /></div>
                  </div>

                  <div className="flow-step-node node-descoped">
                    <div className="node-badge success">Data Tier</div>
                    <div className="node-icon-wrap"><Binary size={22} className="text-emerald" /></div>
                    <div className="node-name">Data Encryption Key (DEK)</div>
                    <div className="node-detail">AES-256-GCM Record Cipher</div>
                  </div>
                </div>

                <div className="diagram-legend-strip">
                  <div className="legend-item">
                    <Info size={15} className="text-cyan" />
                    <span><strong>Envelope Encryption Guarantee:</strong> Even in the event of a database dump, encrypted payloads cannot be decrypted without the cloud HSM key.</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* =========================================================
          ROI & SCOPE REDUCTION CALCULATOR & EXPLANATION (#roi-calculator)
      ========================================================= */}
      <section id="roi-calculator" className="calculator-section section-amber-theme">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-pill pill-amber">
              <Sliders size={14} /> Cost &amp; Risk Modeling
            </span>
            <h2 className="section-title">
              Quantify Your <span className="highlight-amber">Scope &amp; Cost Reduction</span>
            </h2>
            <p className="section-subtitle">
              Calculate how much your enterprise will save on compliance audits, infrastructure complexity, and breach liability.
            </p>
          </div>

          {/* EDUCATIONAL SCOPE EXPLAINER (Clear Context for Buyers) */}
          <div className="scope-explainer-card">
            <div
              className="scope-explainer-header"
              onClick={() => setScopeExplainerOpen(!scopeExplainerOpen)}
              role="button"
              tabIndex={0}
            >
              <div className="explainer-header-left">
                <Info size={20} className="text-emerald" />
                <div>
                  <h4 className="explainer-title">What is Compliance Audit Scope &amp; How Does Tokenization Save $200k+?</h4>
                  <p className="explainer-sub">Click to {scopeExplainerOpen ? 'collapse' : 'expand'} the technical compliance explanation</p>
                </div>
              </div>
              <div className="explainer-chevron">
                {scopeExplainerOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </div>

            {scopeExplainerOpen && (
              <div className="scope-explainer-body">
                <div className="explainer-three-columns">
                  <div className="explainer-col">
                    <h5 className="col-heading text-red">1. The Traditional Problem</h5>
                    <p className="col-desc">
                      Under <strong>PCI DSS 4.0</strong>, <strong>HIPAA</strong>, and <strong>GDPR</strong>, any database, server, log aggregator, or ETL pipeline that touches raw card numbers or PHI is deemed <em>"In Scope"</em>.
                      Qualified Security Assessors (QSAs) must audit, test, and certify every single one of those machines annually.
                    </p>
                  </div>
                  <div className="explainer-col">
                    <h5 className="col-heading text-yellow">2. Why It Costs $200k-$600k/year</h5>
                    <p className="col-desc">
                      Auditors bill $15,000 to $25,000 per week. Engineering teams spend 3 to 6 months every year preparing evidence, configuring network subnets, and managing penetration tests across dozens of servers.
                    </p>
                  </div>
                  <div className="explainer-col">
                    <h5 className="col-heading text-emerald">3. The Vaultize Solution</h5>
                    <p className="col-desc">
                      Vaultize tokenizes raw data at the ingress edge. Your core databases and microservices only store non-sensitive tokens. <strong>QSAs immediately descope your entire downstream infrastructure</strong>, reducing audit fees by up to 90% and virtually eliminating breach liability.
                    </p>
                  </div>
                </div>

                {/* Visual Scope Comparison */}
                <div className="scope-comparison-table">
                  <div className="comparison-card comp-without">
                    <div className="comp-title text-red">Without Tokenization (Standard Storage)</div>
                    <ul className="comp-list">
                      <li>• 25–50+ databases and services remain in PCI/HIPAA audit scope</li>
                      <li>• High annual QSA assessment and penetration testing overhead</li>
                      <li>• Broad blast radius if a database snapshot or log is exposed</li>
                      <li>• Weeks of engineering time spent gathering audit evidence</li>
                    </ul>
                  </div>

                  <div className="comparison-card comp-with">
                    <div className="comp-title text-emerald">With Vaultize Edge Tokenization</div>
                    <ul className="comp-list">
                      <li>• Only the isolated Vaultize service handles raw sensitive data</li>
                      <li>• Downstream databases and analytics stores hold non-sensitive tokens</li>
                      <li>• Database backups and logs contain zero plaintext PAN or PII</li>
                      <li>• Centralized, HMAC-signed audit logs for compliance reviews</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* DYNAMIC CALCULATOR CARD */}
          <div className="calculator-card">
            <div className="calc-left">
              <div className="calc-control-group">
                <label className="calc-label">
                  <span>Monthly Sensitive Records Processed:</span>
                  <span className="calc-val-highlight">{(recordVolume).toLocaleString()} records</span>
                </label>
                <input
                  type="range"
                  min="100000"
                  max="10000000"
                  step="100000"
                  value={recordVolume}
                  onChange={(e) => setRecordVolume(Number(e.target.value))}
                  className="calc-range-slider"
                />
                <div className="slider-ticks">
                  <span>100K</span>
                  <span>2.5M</span>
                  <span>5M</span>
                  <span>10M+</span>
                </div>
              </div>

              <div className="calc-control-group">
                <label className="calc-label">
                  <span>Primary Regulatory Standard:</span>
                </label>
                <div className="calc-options-grid">
                  {['PCI-DSS', 'HIPAA', 'GDPR', 'GENAI-GUARD'].map((std) => (
                    <button
                      key={std}
                      type="button"
                      className={`calc-option-btn ${complianceStandard === std ? 'active' : ''}`}
                      onClick={() => setComplianceStandard(std)}
                    >
                      {std === 'PCI-DSS' && 'PCI DSS 4.0 (Payments)'}
                      {std === 'HIPAA' && 'HIPAA / HITECH (Healthcare)'}
                      {std === 'GDPR' && 'GDPR / DPDP (Data Privacy)'}
                      {std === 'GENAI-GUARD' && 'LLM & AI Data Redaction'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="calc-right">
              <h3 className="calc-summary-title">Estimated Scope &amp; Cost Impact</h3>
              <div className="calc-metric-box">
                <span className="calc-metric-label">Estimated Annual Audit &amp; Ops Savings</span>
                <span className="calc-metric-number text-emerald">${calculatedRoi.annualSavingUsd} / yr</span>
                <span className="calc-metric-sub">Based on reduced QSA audit scope, segmentation, and pen-testing</span>
              </div>

              <div className="calc-mini-grid">
                <div className="mini-stat">
                  <span className="mini-num text-emerald">{calculatedRoi.scopeReduction}%</span>
                  <span className="mini-txt">Systems Removed from Scope</span>
                </div>
                <div className="mini-stat">
                  <span className="mini-num text-cyan">{calculatedRoi.latencyMs}ms</span>
                  <span className="mini-txt">Typical P99 Latency</span>
                </div>
              </div>

              <div className="calc-disclaimer">
                Estimated regulatory &amp; breach exposure reduction: <strong>{calculatedRoi.penaltyAvoidanceUsd}</strong>
              </div>

              <a href="#book-demo-section" className="btn-calc-cta">
                Request a Custom Scope Assessment &rarr;
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          KRYDACLOUD ARCHITECTURE PILLARS (#architecture)
      ========================================================= */}
      <section id="architecture" className="kryda-pillars-section section-teal-theme">
        <div className="container">
          <div className="section-header text-center">
            <div className="kryda-brand-badge-wrap">
              <img src={krydaLogo} alt="Kryda Cloud Technologies" className="kryda-pillars-main-logo" />
            </div>
            <span className="section-pill pill-teal">
              <Building2 size={14} /> Deployment &amp; Engineering
            </span>
            <h2 className="section-title">
              Built for <span className="highlight-teal">Production Infrastructure</span>
            </h2>
            <p className="section-subtitle">
              Backed by Kryda Cloud’s security and cloud engineering practice across Kubernetes, KMS, and enterprise compliance.
            </p>
          </div>

          <div className="pillars-wrapper">
            <div className="pillars-nav">
              {krydaSolutions.map((sol, index) => {
                const IconComponent = sol.icon;
                return (
                  <button
                    key={sol.title}
                    type="button"
                    className={`pillar-tab-btn ${activeAccordion === index ? 'active' : ''}`}
                    onClick={() => setActiveAccordion(index)}
                  >
                    <div className="pillar-btn-icon">
                      <IconComponent size={20} />
                    </div>
                    <div className="pillar-btn-text">
                      <span className="pillar-btn-title">{sol.title}</span>
                      <span className="pillar-btn-sub">{sol.subtitle}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="pillars-content-panel">
              <div className="panel-badge-row">
                <span className="pillar-tag">{krydaSolutions[activeAccordion].tag}</span>
                <span className="pillar-enterprise-tag">Enterprise Ready</span>
              </div>
              <h3 className="panel-title">{krydaSolutions[activeAccordion].title}</h3>
              <h4 className="panel-subtitle">{krydaSolutions[activeAccordion].subtitle}</h4>
              <p className="panel-description">{krydaSolutions[activeAccordion].desc}</p>

              <div className="panel-actions">
                <a
                  href="https://krydacloud.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-panel-learn"
                >
                  About Kryda Cloud &rarr;
                </a>
                <a href="#book-demo-section" className="btn-panel-demo">
                  Talk to an Engineer
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          HIGH-CONVERTING LEAD GENERATION SECTION (#book-demo-section)
      ========================================================= */}
      <section id="book-demo-section" className="lead-generation-section">
        <div className="container">
          <div className="lead-section-grid">
            {/* Left: Value proposition */}
            <div className="lead-info-col">
              <span className="lead-badge">
                <ShieldCheck size={14} /> Architecture Review &amp; Sandbox
              </span>
              <h2 className="lead-heading">
                Talk with a Security Architect &amp; <span className="gradient-text">Start a Pilot</span>
              </h2>
              <p className="lead-subhead">
                Work directly with our engineering team to map your data flows, benchmark latency on your payloads, and spin up an isolated sandbox tenant.
              </p>

              <div className="lead-benefits-list">
                <div className="benefit-item">
                  <div className="benefit-icon"><Check size={16} /></div>
                  <div>
                    <strong>Compliance Scope Review:</strong> Identify which databases and services can be removed from PCI DSS or HIPAA audit scope.
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon"><Check size={16} /></div>
                  <div>
                    <strong>Latency &amp; Throughput Testing:</strong> Benchmark Vaultize against your actual transaction volume and payload structure.
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon"><Check size={16} /></div>
                  <div>
                    <strong>Dedicated Sandbox Tenant:</strong> Get API keys, sample SDKs, and custom schema definitions configured for your team.
                  </div>
                </div>
              </div>

              {/* Secondary Lead Magnet: Whitepaper Download */}
              <div className="whitepaper-download-card">
                <div className="whitepaper-header">
                  <FileText className="text-emerald" size={24} />
                  <div>
                    <h4 className="whitepaper-title">Technical Architecture Guide</h4>
                    <p className="whitepaper-sub">PCI DSS 4.0 Scope Reduction &amp; Field-Level Tokenization</p>
                  </div>
                </div>
                {!whitepaperDownloaded ? (
                  <form onSubmit={handleWhitepaperDownload} className="whitepaper-form">
                    <input
                      type="email"
                      required
                      value={whitepaperEmail}
                      onChange={(e) => setWhitepaperEmail(e.target.value)}
                      placeholder="Work email for PDF guide..."
                      className="whitepaper-input"
                    />
                    <button type="submit" className="btn-whitepaper-submit" disabled={whitepaperLoading}>
                      <Download size={15} />
                      <span>{whitepaperLoading ? 'Sending...' : 'Get PDF'}</span>
                    </button>
                  </form>
                ) : (
                  <div className="whitepaper-success">
                    <CheckCircle2 size={16} className="text-green" />
                    <span>Sent to <strong>{whitepaperEmail}</strong>.</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Inbound Demo Request Form */}
            <div className="lead-form-col">
              <div className="lead-form-card">
                {!leadSubmitted ? (
                  <>
                    <h3 className="form-card-title">Schedule an Architecture Review</h3>
                    <p className="form-card-sub">Our engineering team typically responds within one business day.</p>

                    {leadError && (
                      <div className="lead-error-banner">
                        {leadError}
                      </div>
                    )}

                    <form onSubmit={handleLeadSubmit} className="lead-form">
                      <div className="form-grid-2">
                        <div className="form-field">
                          <label htmlFor="lead-fullname">Full Name *</label>
                          <input
                            id="lead-fullname"
                            type="text"
                            required
                            placeholder="Sarah Jenkins"
                            value={leadForm.fullName}
                            onChange={(e) => setLeadForm({ ...leadForm, fullName: e.target.value })}
                            className="lead-input"
                          />
                        </div>
                        <div className="form-field">
                          <label htmlFor="lead-email">Work Email *</label>
                          <input
                            id="lead-email"
                            type="email"
                            required
                            placeholder="sarah@company.com"
                            value={leadForm.workEmail}
                            onChange={(e) => setLeadForm({ ...leadForm, workEmail: e.target.value })}
                            className="lead-input"
                          />
                        </div>
                      </div>

                      <div className="form-grid-2">
                        <div className="form-field">
                          <label htmlFor="lead-company">Company Name *</label>
                          <input
                            id="lead-company"
                            type="text"
                            required
                            placeholder="Acme Payments Inc."
                            value={leadForm.companyName}
                            onChange={(e) => setLeadForm({ ...leadForm, companyName: e.target.value })}
                            className="lead-input"
                          />
                        </div>
                        <div className="form-field">
                          <label htmlFor="lead-phone">Phone / WhatsApp Number</label>
                          <input
                            id="lead-phone"
                            type="tel"
                            placeholder="+91 79055 45978"
                            value={leadForm.phoneNumber}
                            onChange={(e) => setLeadForm({ ...leadForm, phoneNumber: e.target.value })}
                            className="lead-input"
                          />
                        </div>
                      </div>

                      <div className="form-grid-2">
                        <div className="form-field">
                          <label htmlFor="lead-role">Role</label>
                          <select
                            id="lead-role"
                            value={leadForm.jobRole}
                            onChange={(e) => setLeadForm({ ...leadForm, jobRole: e.target.value })}
                            className="lead-select"
                          >
                            <option value="CISO / VP Security">CISO / VP Security</option>
                            <option value="Lead Architect">Principal / Lead Architect</option>
                            <option value="Engineering Manager">VP / Director of Engineering</option>
                            <option value="Compliance Officer">Compliance / Risk Officer</option>
                            <option value="Senior Developer">Security / Backend Engineer</option>
                          </select>
                        </div>
                        <div className="form-field">
                          <label htmlFor="lead-size">Company Size</label>
                          <select
                            id="lead-size"
                            value={leadForm.companySize}
                            onChange={(e) => setLeadForm({ ...leadForm, companySize: e.target.value })}
                            className="lead-select"
                          >
                            <option value="1-50">1 – 50 employees</option>
                            <option value="51-200">51 – 200 employees</option>
                            <option value="201-1000">201 – 1,000 employees</option>
                            <option value="1000+">1,000+ employees</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-field">
                        <label htmlFor="lead-usecase">Primary Use Case</label>
                        <select
                          id="lead-usecase"
                          value={leadForm.primaryUseCase}
                          onChange={(e) => setLeadForm({ ...leadForm, primaryUseCase: e.target.value })}
                          className="lead-select"
                        >
                          <option value="PCI-DSS">PCI DSS 4.0 Card Tokenization &amp; Scope Reduction</option>
                          <option value="GENAI">LLM &amp; AI Prompt Redaction</option>
                          <option value="HIPAA">HIPAA / Healthcare PHI Protection</option>
                          <option value="GDPR">GDPR / DPDP Data Privacy &amp; Masking</option>
                          <option value="FPE">Format-Preserving Encryption for Databases</option>
                        </select>
                      </div>

                      <div className="form-field">
                        <label htmlFor="lead-message">Project Context (Optional)</label>
                        <textarea
                          id="lead-message"
                          rows={3}
                          placeholder="Tell us about your current stack, compliance timeline, or expected throughput..."
                          value={leadForm.message}
                          onChange={(e) => setLeadForm({ ...leadForm, message: e.target.value })}
                          className="lead-textarea"
                        />
                      </div>

                      <button
                        type="submit"
                        className="btn-submit-lead"
                        disabled={leadLoading}
                      >
                        {leadLoading ? 'Submitting...' : 'Request Architecture Review'}
                      </button>
                      <p className="form-terms-note">
                        Your information is kept confidential and used only to prepare for your session.
                      </p>
                    </form>
                  </>
                ) : (
                  <div className="lead-success-screen">
                    <div className="success-icon-badge">
                      <CheckCircle2 size={48} className="text-emerald" />
                    </div>
                    <h3 className="success-title">Consultation Confirmed!</h3>
                    <p className="success-message">
                      Thank you, <strong>{leadForm.fullName}</strong>. A Kryda Cloud Principal Security Architect has been assigned to your request.
                    </p>

                    <div className="ref-code-box">
                      <span className="ref-label">Your Reference Code:</span>
                      <span className="ref-value font-mono">{leadRefCode}</span>
                    </div>

                    <p className="success-next-steps">
                      We will review your submission and contact you at <strong>{leadForm.workEmail}</strong> within 4 business hours with calendar availability and your staging credentials.
                    </p>

                    <div className="success-actions">
                      <button
                        type="button"
                        className="btn-primary-glow"
                        onClick={() => navigate('/plans')}
                      >
                        Review Commercial Plans
                      </button>
                      <a
                        href="#simulator"
                        className="btn-glass"
                      >
                        Explore Simulator
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          FAQ ACCORDION (#faq)
      ========================================================= */}
      <section id="faq" className="faq-section">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-pill">
              <HelpCircle size={14} /> Frequently Asked Questions
            </span>
            <h2 className="section-title">
              Got Questions? <span className="gradient-text">We Have Answers</span>
            </h2>
          </div>

          <div className="faq-list">
            {faqs.map((faq, idx) => (
              <div
                key={faq.q}
                className={`faq-card ${openFaq === idx ? 'open' : ''}`}
                onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                role="button"
                tabIndex={0}
              >
                <div className="faq-question-row">
                  <span className="faq-q-text">{faq.q}</span>
                  <span className="faq-toggle-icon">
                    {openFaq === idx ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </span>
                </div>
                {openFaq === idx && (
                  <div className="faq-answer-row">
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================
          FINAL CTA BANNER
      ========================================================= */}
      <section className="final-cta-section">
        <div className="container">
          <div className="final-cta-box">
            <h2 className="final-cta-title">Ready to Elevate Your Security Posture?</h2>
            <p className="final-cta-subtitle">
              Join leading fintech, healthcare, and enterprise platforms securing data with Vaultize &amp; Kryda Cloud Technologies.
            </p>
            <div className="final-cta-buttons">
              <a href="#book-demo-section" className="btn-primary-glow">
                Schedule Architecture Consultation
              </a>
              <button
                type="button"
                className="btn-glass"
                onClick={() => navigate('/signup')}
              >
                Launch Free Developer Tier (1,000 Tokens)
              </button>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default Home;
